/**
 * Admin Messages Service — Support Inbox.
 *
 * Reads/writes the same Appwrite tables the Flask backend's messaging
 * routes use (conversations / conversation_participants / messages), but
 * directly via the Appwrite SDK — the backend has no admin-wide "list every
 * support ticket" endpoint (its /conversations route only returns
 * conversations the calling X-User-Id is already a participant in, which
 * doesn't fit an admin inbox). This matches the same direct-Appwrite
 * pattern already used by adminUserService.js/adminOrderService.js for
 * other "view across all users" admin pages.
 */
import {
  tablesDB, DATABASE_ID, Query, ID,
  CONVERSATIONS_TABLE_ID, CONVERSATION_PARTICIPANTS_TABLE_ID, MESSAGES_TABLE_ID,
} from '../lib/appwrite';
import AdminAuthService from './adminAuthService';
import { getUser, getAdminUsers } from './adminUserService';

const now = () => new Date().toISOString();

function _rows(res) {
  return res.rows || [];
}

// ── Ticket list ───────────────────────────────────────────────

export async function listSupportTickets({ status, category, priority, assignedTo } = {}) {
  // Sorted client-side, not via Query.orderDesc — lastMessageAt isn't
  // guaranteed indexed for ordering (the backend's own list_conversations
  // does the same rather than risk an Appwrite error on an unindexed sort).
  const queries = [Query.equal('type', 'support'), Query.limit(200)];
  if (status)   queries.push(Query.equal('status', status));
  if (category) queries.push(Query.equal('category', category));
  if (priority) queries.push(Query.equal('priority', priority));
  if (assignedTo && assignedTo !== 'unassigned') queries.push(Query.equal('assignedTo', assignedTo));

  const res = await tablesDB.listRows(DATABASE_ID, CONVERSATIONS_TABLE_ID, queries);
  let tickets = _rows(res);
  if (assignedTo === 'unassigned') {
    tickets = tickets.filter((t) => !t.assignedTo);
  }
  return tickets.sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''));
}

// ── Ticket detail ─────────────────────────────────────────────

export async function getTicketDetail(conversationId) {
  const [ticket, partsRes, msgsRes] = await Promise.all([
    tablesDB.getRow(DATABASE_ID, CONVERSATIONS_TABLE_ID, conversationId),
    tablesDB.listRows(DATABASE_ID, CONVERSATION_PARTICIPANTS_TABLE_ID, [
      Query.equal('conversationId', conversationId),
      Query.limit(50),
    ]),
    tablesDB.listRows(DATABASE_ID, MESSAGES_TABLE_ID, [
      Query.equal('conversationId', conversationId),
      Query.orderAsc('createdAt'),
      Query.limit(500),
    ]),
  ]);
  return { ticket, participants: _rows(partsRes), messages: _rows(msgsRes) };
}

export async function getTicketMessages(conversationId) {
  const { messages } = await getTicketDetail(conversationId);
  return messages || [];
}

export async function getTicketParticipants(conversationId) {
  const { participants } = await getTicketDetail(conversationId);
  return participants || [];
}

// ── Participants ──────────────────────────────────────────────

async function _findParticipant(conversationId, participantId) {
  const res = await tablesDB.listRows(DATABASE_ID, CONVERSATION_PARTICIPANTS_TABLE_ID, [
    Query.equal('conversationId', conversationId),
    Query.equal('participantId', participantId),
    Query.limit(1),
  ]);
  return _rows(res)[0] || null;
}

async function _ensureParticipant(conversationId, participantId, role = 'member') {
  if (!participantId) return;
  const existing = await _findParticipant(conversationId, participantId);
  if (existing) return;
  await tablesDB.createRow(DATABASE_ID, CONVERSATION_PARTICIPANTS_TABLE_ID, ID.unique(), {
    conversationId,
    participantId,
    role,
    joinedAt: now(),
    lastReadAt: now(),
  });
}

// ── Send message ──────────────────────────────────────────────

export async function sendAdminMessage(conversationId, body) {
  const agentId = await AdminAuthService.getCurrentUserId();
  const text = (body || '').trim();
  if (!text) throw new Error('Message body required');

  const message = await tablesDB.createRow(DATABASE_ID, MESSAGES_TABLE_ID, ID.unique(), {
    conversationId,
    senderId: agentId,
    body: text,
    messageType: 'text',
    status: 'sent',
    createdAt: now(),
  });

  const preview = text.length > 280 ? `${text.slice(0, 280)}…` : text;
  await tablesDB.updateRow(DATABASE_ID, CONVERSATIONS_TABLE_ID, conversationId, {
    lastMessageAt: now(),
    lastMessageText: preview,
  });
  // So the customer's own app (scoped to "conversations I'm a participant
  // in") can see this agent as a real participant, not just a stray sender.
  await _ensureParticipant(conversationId, agentId, 'member');

  return message;
}

// ── Ticket lifecycle ──────────────────────────────────────────

export async function updateTicketStatus(conversationId, status) {
  const data = { status };
  if (status === 'resolved') data.resolvedAt = now();
  await tablesDB.updateRow(DATABASE_ID, CONVERSATIONS_TABLE_ID, conversationId, data);
}

export async function claimTicket(conversationId) {
  const agentId = await AdminAuthService.getCurrentUserId();
  if (!agentId) throw new Error('Not authenticated');
  await tablesDB.updateRow(DATABASE_ID, CONVERSATIONS_TABLE_ID, conversationId, { assignedTo: agentId });
  await _ensureParticipant(conversationId, agentId, 'member');
}

export async function assignTicket(conversationId, agentId) {
  if (!agentId) throw new Error('agentId required');
  await tablesDB.updateRow(DATABASE_ID, CONVERSATIONS_TABLE_ID, conversationId, { assignedTo: agentId });
  await _ensureParticipant(conversationId, agentId, 'member');
}

// ── Agents ────────────────────────────────────────────────────

export async function listAgents() {
  try {
    return await getAdminUsers();
  } catch {
    return [];
  }
}

// ── User info cache ───────────────────────────────────────────

const _userCache = {};

export async function getUserInfo(userId) {
  if (!userId) return {};
  if (_userCache[userId]) return _userCache[userId];
  try {
    const user = await getUser(userId);
    _userCache[userId] = user || {};
    return _userCache[userId];
  } catch {
    return {};
  }
}
